import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useZones, useLocations } from '../hooks/useWmsLocations';
import {
  useSerializedItem,
  useSerializedItemMovements,
  useTransferSerializedItem,
  useChangeDeviceStatus,
} from '../hooks/useWmsInventory';
import { DeviceStatusBadge, SerializedItemStatusBadge } from '../components/shared/StatusBadge';
import { SerializedItemTransferModal } from '../components/serials/SerializedItemTransferModal';
import { DeviceStatusChangeModal } from '../components/serials/DeviceStatusChangeModal';
import type {
  SerializedItem,
  ChangeDeviceStatusInput,
  ZonesResponse,
  LocationsResponse,
  MovementsResponse,
} from '../types';

export default function SerializedItemDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  // Queries
  const itemQuery = useSerializedItem(id || '');
  const movementsQuery = useSerializedItemMovements(id || '', { page_length: 50 });
  const zonesQuery = useZones();
  const locationsQuery = useLocations({ page_length: 100 });

  // Mutations
  const transferMutation = useTransferSerializedItem();
  const changeStatusMutation = useChangeDeviceStatus();

  const item = itemQuery.data as SerializedItem | undefined;
  const movementsData = movementsQuery.data as MovementsResponse | undefined;
  const zonesData = zonesQuery.data as ZonesResponse | undefined;
  const locationsData = locationsQuery.data as LocationsResponse | undefined;

  const zones = zonesData?.zones || [];
  const locations = locationsData?.locations || [];
  const movements = movementsData?.movements || [];

  const handleTransfer = async (locationId: string) => {
    if (!item) return;
    await transferMutation.mutateAsync({ id: item.id, toLocationId: locationId });
  };

  const handleChangeStatus = async (data: ChangeDeviceStatusInput) => {
    if (!item) return;
    await changeStatusMutation.mutateAsync({ id: item.id, data });
  };

  if (itemQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (itemQuery.error || !item) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-destructive">Item not found</h2>
        <p className="text-muted-foreground mt-2">
          The serialized item you're looking for doesn't exist.
        </p>
        <Button asChild className="mt-4">
          <Link to="/wms/serials">Back to Items</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Link
              to="/wms/serials"
              className="text-muted-foreground hover:text-foreground"
            >
              Serialized Items
            </Link>
            <span className="text-muted-foreground">/</span>
            <h1 className="text-2xl font-bold tracking-tight font-mono">
              {item.serial_number}
            </h1>
          </div>
          <p className="text-muted-foreground mt-1">{item.sku_code}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowStatusModal(true)}
          >
            Change Status
          </Button>
          <Button onClick={() => setShowTransferModal(true)}>Transfer</Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Status Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">Inventory</span>
                <SerializedItemStatusBadge status={item.item_status} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">5S Status</span>
                {item.device_status ? (
                  <DeviceStatusBadge status={item.device_status} />
                ) : (
                  <span className="text-muted-foreground text-sm">—</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            {item.full_location_code ? (
              <Badge variant="outline" className="font-mono text-lg">
                {item.full_location_code}
              </Badge>
            ) : (
              <span className="text-muted-foreground">Not assigned</span>
            )}
            {item.zone_name && (
              <p className="text-sm text-muted-foreground mt-1">{item.zone_name}</p>
            )}
          </CardContent>
        </Card>

        {/* Identifiers Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Identifiers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {item.mac_address && (
              <div>
                <span className="text-xs text-muted-foreground">MAC:</span>
                <span className="font-mono text-sm ml-2">{item.mac_address}</span>
              </div>
            )}
            {item.dev_id && (
              <div>
                <span className="text-xs text-muted-foreground">ID:</span>
                <span className="font-mono text-sm ml-2">{item.dev_id}</span>
              </div>
            )}
            {!item.mac_address && !item.dev_id && (
              <span className="text-muted-foreground text-sm">None</span>
            )}
          </CardContent>
        </Card>

        {/* Calibration Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Calibration
            </CardTitle>
          </CardHeader>
          <CardContent>
            {item.calibration_due_date ? (
              <div className="space-y-1">
                <div>
                  <span className="text-xs text-muted-foreground">Due:</span>
                  <span className="text-sm ml-2">
                    {new Date(item.calibration_due_date).toLocaleDateString()}
                  </span>
                </div>
                {item.last_calibration_date && (
                  <div>
                    <span className="text-xs text-muted-foreground">Last:</span>
                    <span className="text-sm ml-2">
                      {new Date(item.last_calibration_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground text-sm">N/A</span>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Details Card */}
      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">SKU</span>
              <p className="font-medium">{item.sku_code}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Firmware</span>
              <p className="font-medium">{item.firmware_version || '—'}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Hardware</span>
              <p className="font-medium">{item.hardware_version || '—'}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Manufacture Date</span>
              <p className="font-medium">
                {item.manufacture_date
                  ? new Date(item.manufacture_date).toLocaleDateString()
                  : '—'}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Created</span>
              <p className="font-medium">
                {new Date(item.created_at).toLocaleDateString()}
              </p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Updated</span>
              <p className="font-medium">
                {new Date(item.updated_at).toLocaleDateString()}
              </p>
            </div>
            {item.notes && (
              <div className="col-span-2">
                <span className="text-sm text-muted-foreground">Notes</span>
                <p className="font-medium">{item.notes}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Movement History */}
      <Card>
        <CardHeader>
          <CardTitle>Movement History</CardTitle>
        </CardHeader>
        <CardContent>
          {movementsQuery.isLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : movements.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No movement history
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((movement) => (
                  <TableRow key={movement.id}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(movement.performed_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {movement.movement_type.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {movement.reason_code && (
                        <span className="text-sm">
                          Reason: {movement.reason_code.replace('_', ' ')}
                        </span>
                      )}
                      {movement.notes && (
                        <p className="text-sm text-muted-foreground">
                          {movement.notes}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      {movement.performed_by_name || movement.performed_by}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <SerializedItemTransferModal
        open={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onTransfer={handleTransfer}
        item={item}
        zones={zones}
        locations={locations}
        isLoading={transferMutation.isPending}
      />

      <DeviceStatusChangeModal
        open={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        onChangeStatus={handleChangeStatus}
        item={item}
        isLoading={changeStatusMutation.isPending}
      />
    </div>
  );
}
